import { fetchAction } from '../../actions';
import { createSelector } from 'reselect';
// Modules
import { types as companyTypes } from '../companies';
import { Reservation, Rider, Vehicle, VehicleType } from '../api-types';
import { defaultProjectKey, driverProjectKey } from '../ticketing';
import { paths, createPath } from '../../../routes/paths';
// Ressources
import URLUtils from '../../../utils/urlUtils';
import DateUtils from '../../../utils/dateUtils';
import ObjectUtils from '../../../utils/objectUtils';
import CoreAPIService from '../../../utils/core-api';
import envConfig from '../../../config';
import { DEFAULT_ROW_NUMBER } from '../../../utils/constants';

export enum API_ENDPOINT {
    ID = 'id',
    BILLING = 'billing',
    PRICING = 'pricing',
    TICKETING = 'ticketing',
    PRIVAPI = 'privapi',
}

export interface TableRequestPoint {
    url: string;
    reducer: string;
    type?: API_ENDPOINT;
    dataKey?: string;
    filters?: any;
    preload?: boolean;
    query?: 'get' | 'post';
    handleRow?: (id, row) => string; // path used when row is clicked
}

const unTypedTableRequestPoints = {
    BOOKINGS: {
        preload: true,
        url: 'bookings',
        reducer: 'bookings_list',
        handleRow: (id) => `${paths.bookings}/${id}`,
    },
    RESERVATIONS: {
        preload: true,
        url: 'reservations',
        reducer: 'reservations_list',
        handleRow: (id) => `${paths.reservations}/${id}`,
    },
    RESERVATIONS_RECURRENT: {
        url: 'recurrents',
        reducer: 'reservations_recurrent_list',
    },
    PARTNERS: {
        url: 'partners',
        reducer: 'partners_list',
        handleRow: (id) => `${paths.partners}/${id}`,
    },
    DRIVERS: { url: 'drivers', reducer: 'drivers_list', handleRow: (id) => `${paths.drivers}/${id}` },
    // Active drivers with an issue (filter "driver.issue" of the core), one reducer per issue
    // so each tab of the page keeps its own rows and filters
    DRIVERS_ISSUE_DEFAULT_PREBOOK_GROUP: {
        url: 'drivers',
        reducer: 'drivers_issue_default_prebook_group_list',
        handleRow: (id) => `${paths.drivers}/${id}`,
    },
    DRIVERS_ISSUE_NO_CONTROL_TOWER: {
        url: 'drivers',
        reducer: 'drivers_issue_no_control_tower_list',
        handleRow: (id) => `${paths.drivers}/${id}`,
    },
    DRIVERS_ISSUE_NO_DRIVING_LICENCE: {
        url: 'drivers',
        reducer: 'drivers_issue_no_driving_licence_list',
        handleRow: (id) => `${paths.drivers}/${id}`,
    },
    VEHICLES: {
        url: 'vehicles',
        reducer: 'vehicles_list',
        handleRow: (id) => `${paths.vehicles}/${id}`,
    },
    VEHICLES_TYPES: { url: 'vehicles/models', reducer: 'vehicles_types_list' },
    RIDERS: { url: 'riders', reducer: 'riders_list', handleRow: (id) => `${paths.riders}/${id}` },
    COMPANIES: {
        url: 'companies',
        reducer: 'companies_list',
        handleRow: (id) => `${paths.companies}/${id}`,
    },
    PARTNER_BILLS: {
        url: 'partner_bills',
        reducer: 'partner_bills_list',
        handleRow: (id) => `${paths.billsPartner}/${id}`,
    },
    BILLS_SIMPLE: { url: 'simple_bills', reducer: 'bills_simple_list' },
    BILLS_GROUPED: { url: 'period_bills', reducer: 'bills_grouped_list' },
    BILLS_CREDITS: { url: 'credit_notes', reducer: 'bills_credit_list' },
    PREBOOKS: {
        preload: true,
        url: 'prebook/groups',
        reducer: 'prebooks_list',
    },
    RANKS: {
        url: 'ranks',
        reducer: 'ranks_list',
    },
    MARKETING_PROMO: {
        url: 'promotions',
        reducer: 'marketing_promos',
        handleRow: (id) => `${paths.marketingPromosAdd}?id=${id}`,
    },

    // PRICING
    COMPANY_TO_PRICING_SETS: {
        url: 'companyToPricingSets/search',
        type: API_ENDPOINT.PRICING,
        reducer: 'sets',
    },
    GROUP_TO_PRICING_SETS: {
        url: 'groupToPricingSets/search',
        type: API_ENDPOINT.PRICING,
        // Its own slot, not 'sets'. Table state is keyed by this string, and the
        // sheet page mounts both tables at once - sharing it made each render
        // the other's rows through its own headers.
        reducer: 'group_sets',
    },

    // BILLING
    PARTNER_BILLS_BETA: {
        url: 'partnerBills/search',
        type: API_ENDPOINT.BILLING,
        // filters: { type: 'month' },
        reducer: 'partner_bills_list',
        handleRow: (id) => `${paths.billsPartner}/${id}`,
    },
    BILLS_SIMPLE_BETA: {
        url: 'customerBills/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'ride' },
        reducer: 'bills_simple_list',
    },
    BILLS_GROUPED_BETA: {
        url: 'customerBills/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'month', user_id: null },
        reducer: 'bills_grouped_list',
    },
    BILLS_GROUPED_PENDING_BETA: {
        url: 'customerBills/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'month', user_id: null },
        reducer: 'bills_grouped_pending_list',
    },
    BILLS_CREDITS_BETA: {
        url: 'creditNotes/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'ride' },
        reducer: 'bills_credit_list',
    },
    BILLS_CREDITS_MONTH: {
        url: 'creditNotes/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'month' },
        reducer: 'bills_credit_month_list',
    },
    BILLS_CREDITS_EXCEPTIONAL: {
        url: 'creditNotes/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'exceptional' },
        reducer: 'bills_credit_exceptional_list',
    },
    BILLS_EXCEPTIONALS: {
        url: 'customerBills/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'exceptional' },
        reducer: 'bills_exceptional_list',
    },
    BILLS_RECTIFICATIONS: {
        url: 'customerBills/search',
        type: API_ENDPOINT.BILLING,
        filters: { type: 'correction', user_id: null },
        reducer: 'bills_rectification_list',
    },

    // TICKETING
    TICKETING_ISSUES_CUSTOMER: {
        url: `projects/${defaultProjectKey}/issues`,
        dataKey: 'issues',
        type: API_ENDPOINT.TICKETING,
        handleRow: (id, row) =>
            `${createPath(paths.ticketingProjectAdd, { project: defaultProjectKey })}?id=${id}&author_id=${
                row.author?.id
            }`,
        reducer: 'issues_list',
    },
    TICKETING_ISSUES_DRIVER: {
        url: `projects/${driverProjectKey}/issues`,
        dataKey: 'issues',
        type: API_ENDPOINT.TICKETING,
        handleRow: (id, row) =>
            `${createPath(paths.ticketingProjectAdd, { project: driverProjectKey })}?id=${id}&author_id=${
                row.author?.id
            }`,
        reducer: 'issues_list',
    },

    // ID

    ID_EMPLOYEES: {
        url: 'internal/backoffice/employee/search',
        reducer: 'employees',
        type: API_ENDPOINT.ID,
    },

    // PRIVAPI

    RIDES: {
        url: 'internal/rides',
        reducer: 'rides_list',
        type: API_ENDPOINT.PRIVAPI,
        // filters: {
        //     status: [
        //         'ACCEPTED',
        //         'DISPATCH_TIMEOUT',
        //         'ALLOCATED',
        //         'PROCESSING',
        //         'SEEN',
        //         'RECEIVED',
        //         'ON_WAY',
        //         'PASSENGER_ON_BOARD',
        //         'AT_PICKUP',
        //         'FINISHED',
        //         'CANCELLED',
        //         'CANCELLATION_NO_CHARGE',
        //         'CANCELLATION_WITH_CHARGE',
        //     ],
        // },
        handleRow: (id) => `${paths.rides}/${id}`,
    },
    USERS: {
        url: 'internal/user/list',
        reducer: 'users_list',
        type: API_ENDPOINT.PRIVAPI,
        handleRow: (id) => `${paths.users}/${id}`,
    },
    CONTRACTS: {
        url: 'internal/contract/list',
        reducer: 'contracts_list',
        type: API_ENDPOINT.PRIVAPI,
        handleRow: (id, row) => `${paths.contracts}/${row.id_contract}`,
    },
};

// Define the type for the keys of tableRequestPoints
type RequestKey = keyof typeof unTypedTableRequestPoints;

// Define the final typed object
export const tableRequestPoints: { [K in RequestKey]: TableRequestPoint } = unTypedTableRequestPoints;

/**
 * `URLUtils.urlToFilter` drops falsy values, so a filter left blank — a search
 * box the operator emptied, a select back on "all" — reaches the endpoint as no
 * filter at all. The list endpoints that demand one then answer 400 "At least
 * one filter must be send", so count what will actually be sent, not the keys.
 */
function hasUsableFilter(filters: any): boolean {
    return Object.values(filters || {}).some((value) =>
        Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '',
    );
}

export const name: string = 'table';

export const types = {
    SKIP: name + '/SKIP',
    GET_PAGES: name + '/GET_PAGES',
    RESET_FILTERS: name + '/RESET_FILTERS',
};

interface TableState {
    bookings_list: {};
    reservations_list: Partial<Reservation>[];
    rides_list: [];
    reservations_recurrent_list: {};
    partners_list: {};
    drivers_list: {};
    vehicles_list: Vehicle[];
    vehicles_types_list: VehicleType[];
    riders_list: Partial<Rider>[];
    users_list: Partial<Rider>[];
    contracts_list: {};
    contracts_departments: {};
    contracts_memberships: any;
    contracts_contracts: {};
    companies_list: {};
    companies_departments: {};
    companies_memberships: any;
    companies_contracts: {};
    partner_bills_list: {};
    bills_simple_list: {};
    bills_grouped_list: {};
    bills_grouped_pending_list: {};
    bills_credit_list: {};
    bills_credit_month_list: {};
    bills_credit_exceptional_list: {};
    bills_exceptional_list: {};
    bills_rectification_list: {};
    issues_list: {};
    prebooks_list: {};
    ranks_list: {};
    saved_filters: {};
    marketing_promos: {};
    employees: {};
}

const initialState: TableState = {
    bookings_list: {},
    reservations_list: [],
    reservations_recurrent_list: {},
    rides_list: [],
    partners_list: {},
    drivers_list: {},
    vehicles_list: [],
    vehicles_types_list: [],
    riders_list: [],
    users_list: [],
    contracts_list: {},
    contracts_departments: {},
    contracts_memberships: {},
    contracts_contracts: {},
    companies_list: {},
    companies_departments: {},
    companies_memberships: {},
    companies_contracts: {},
    partner_bills_list: {},
    bills_simple_list: {},
    bills_grouped_list: {},
    bills_grouped_pending_list: {},
    bills_credit_list: {},
    bills_credit_month_list: {},
    bills_credit_exceptional_list: {},
    bills_exceptional_list: {},
    bills_rectification_list: {},
    issues_list: {},
    prebooks_list: {},
    ranks_list: {},
    saved_filters: {},
    marketing_promos: {},
    employees: {},
};

const reducer = (state = initialState, action): TableState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const mkpc = parameters?.mkpc;
    const savedFilters = parameters?.filters;
    switch (action.type) {
        case types.SKIP:
            return {
                ...state,
                [payload.mkpc.reducer]: { data: [], meta: { total: 0 } },
            };
        case types.GET_PAGES:
            let formattedPayload = payload;
            if (mkpc.type === API_ENDPOINT.TICKETING) {
                formattedPayload = {
                    data: payload[mkpc.dataKey],
                    meta: { total: payload.total },
                };
            } else if (mkpc.type === API_ENDPOINT.ID) {
                if (payload.meta) {
                    formattedPayload = payload;
                } else {
                    formattedPayload = {
                        data: payload,
                        meta: { total: payload?.length ?? 0 },
                    };
                }
            }

            return {
                ...state,
                [mkpc.reducer]: formattedPayload,
                saved_filters: {
                    ...state.saved_filters,
                    [`${mkpc.url}_${mkpc.reducer}`]: savedFilters,
                },
            };
        case types.RESET_FILTERS:
            const newStateResetFilters = ObjectUtils.deepCopy(state);
            newStateResetFilters.saved_filters[`${mkpc.url}_${mkpc.reducer}`] = {};
            return newStateResetFilters;
        case companyTypes.EDIT_COMPANY_MEMBER:
            const idm = parameters?.idm;
            const params = parameters?.payload;
            const newState = ObjectUtils.deepCopy(state);
            const index = newState.companies_memberships.data.findIndex((m) => m.rider?.id === idm);
            const current = newState.companies_memberships.data[index];

            Object.keys(params).forEach((k) => {
                const val = params[k];
                if (k === 'rider_dispatch_groups_authorizations') {
                    const idx = current.rider.rider_dispatch_groups_authorizations.findIndex(
                        (o) => o.dispatch_group.id === val.id,
                    );
                    if (idx !== -1) {
                        current.rider.rider_dispatch_groups_authorizations[idx].authorization_status = val.value;
                    } else {
                        current.rider.rider_dispatch_groups_authorizations.push({
                            authorization_status: val.value,
                            dispatch_group: val,
                        });
                    }
                } else if (k === 'department_id') {
                    current.department = payload.department;
                } else {
                    current[k] = val;
                }
            });
            return newState;
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    resetFilters: (type: TableRequestPoint) => {
        return {
            type: types.RESET_FILTERS,
            parameters: { mkpc: type },
        };
    },
    getPages: (page: number, type: TableRequestPoint, order?: any, filters?: any, rowsPerPage?: number) => {
        if (type?.type === API_ENDPOINT.BILLING || type?.type === API_ENDPOINT.PRICING) {
            let payload: any = {
                page: page + 1,
                num_per_page: rowsPerPage ?? DEFAULT_ROW_NUMBER,
            };
            if (order?.property) {
                payload.sort = order.property;
                payload.order = order.nextOrder;
            }

            if (filters) {
                const parseKey = (key) => key.match(/(.*?)\[(.*?)\]/)?.[2] ?? key;

                const outputFilters = Object.entries(filters).reduce(
                    (acc, [key, value]) => {
                        if (key.startsWith('before') && value) {
                            return {
                                ...acc,
                                before: { ...acc.before, [parseKey(key)]: value },
                            };
                        }
                        if (key.startsWith('after') && value) {
                            return {
                                ...acc,
                                after: { ...acc.after, [parseKey(key)]: value },
                            };
                        }
                        if (key.startsWith('in') && value) {
                            return {
                                ...acc,
                                in: { ...acc.in, [parseKey(key)]: value },
                            };
                        }
                        return {
                            ...acc,
                            filters: {
                                ...acc.filters,
                                ...(value && { [parseKey(key)]: value }),
                            },
                        };
                    },
                    { after: {}, before: {}, filters: {}, in: {} },
                );

                payload = { ...payload, ...outputFilters };
            }
            if (type?.filters) {
                payload.filters = { ...payload.filters, ...type.filters };
            }

            const savedFilters = { ...filters, ...type.filters, page: page + 1 };

            return fetchAction(CoreAPIService.post(`/core/${type.type}/${type.url}`, payload), types.GET_PAGES, {
                mkpc: type,
                filters: savedFilters,
            });
        } else if (type?.type === API_ENDPOINT.ID) {
            let payload: any = {};
            const isGetQuery = type.query === 'get';

            if (filters) {
                const parseKey = (key) => key.match(/(.*?)\[(.*?)\]/)?.[2] ?? key;

                const outputFilters = Object.entries(filters).reduce(
                    (acc, [key, value]) => {
                        return {
                            ...acc,
                            filters: {
                                ...acc.filters,
                                ...(value && isGetQuery ? { [`search[${key}]`]: value } : { [parseKey(key)]: value }),
                            },
                        };
                    },
                    { filters: {} },
                );

                payload = { ...payload, ...outputFilters };
            }
            if (type?.filters) {
                payload.filters = { ...payload.filters, ...type.filters };
            }

            const savedFilters = { ...filters, ...type.filters };

            if (order && isGetQuery) {
                payload.filters.sort = order.property;
                payload.filters.order = order.nextOrder;
                savedFilters.sort = order.property;
                savedFilters.order = order.nextOrder;
                savedFilters.page = page + 1;
            }

            return fetchAction(
                isGetQuery
                    ? CoreAPIService.get(
                          `/${type.url}?pagination[num_per_page]=${rowsPerPage}&pagination[page]=${
                              page + 1
                          }${URLUtils.urlToFilter(payload.filters)}`,
                          null,
                          { credential: true },
                          envConfig.id_url,
                      )
                    : CoreAPIService.post(`/${type.url}`, payload, { credential: true }, envConfig.id_url),
                types.GET_PAGES,
                {
                    mkpc: type,
                    filters: savedFilters,
                },
            );
        } else if (type?.type === API_ENDPOINT.TICKETING) {
            let outputFilters = {};
            if (filters) {
                outputFilters = Object.entries(filters).reduce((acc, [key, value]) => {
                    if (key.endsWith('_at') && value) {
                        const keyMin = `${key}_min`;
                        const keyMax = `${key}_max`;
                        const date = new Date(value as string);
                        date.setDate(date.getDate() + 1);
                        return {
                            ...acc,
                            [keyMin]: value,
                            [keyMax]: DateUtils.dateFormat(date, 'yyyy-mm-dd'),
                        };
                    } else if (key === 'id' && value) {
                        return {
                            ...acc,
                            'ids[0]': value,
                        };
                    }
                    return {
                        ...acc,
                        ...(value && { [key]: value }),
                    };
                }, {});
            }

            const allFilters = { ...outputFilters, ...type.filters, offset: 20 * page };
            const savedFilters = { ...filters, ...type.filters, page: page + 1 };

            if (order?.property) {
                const sortString = order.property.endsWith(`:${order.nextOrder}`)
                    ? order.property
                    : `${order.property}:${order.nextOrder}`;
                allFilters.sort = sortString;
                savedFilters.sort = sortString;
            }

            // console.log('=>', allFilters, savedFilters);
            return fetchAction(
                CoreAPIService.get(`/ticketing/${type.url}?${URLUtils.urlToFilter(allFilters)}`, null, {
                    headers: { token: 'manager1' },
                }),
                types.GET_PAGES,
                {
                    mkpc: type,
                    filters: savedFilters,
                },
            );
        } else if (type?.type === API_ENDPOINT.PRIVAPI) {
            // const customFilters = pickBy(filters, Boolean);
            const formatFilters = { ...type.filters, ...filters };
            // console.log(type.filters, filters, { formatFilters });

            if (!hasUsableFilter(formatFilters)) {
                return { type: types.SKIP, payload: { mkpc: type } };
            }

            Object.entries(formatFilters).forEach(([key, value]) => {
                if (value && (key.startsWith('before') || key.startsWith('after'))) {
                    console.log(value, new Date(value as string));
                    formatFilters[key] = DateUtils.dateToSQlDateTime(new Date(value as string));
                }
                if (value && (key.endsWith('min') || key.endsWith('max'))) {
                    console.log(value, new Date(value as string));
                    formatFilters[key] = new Date(value as string).getTime() / 1000;
                }
            });

            const savedFilters = {
                page: page + 1,
                ...filters,
            } as any;

            if (order) {
                formatFilters.sort = order.property;
                formatFilters.order = order.nextOrder;
                // formatFilters[order.property] = order.nextOrder; // key=order
                savedFilters.sort = order.property;
                savedFilters.order = order.nextOrder;
            }

            return fetchAction(
                CoreAPIService.get(
                    `${type.url}?pagination[num_per_page]=${rowsPerPage}&pagination[page]=${
                        page + 1
                    }${URLUtils.urlToFilter(formatFilters)}`,
                    null,
                    null,
                    envConfig.privapi_url,
                ),
                types.GET_PAGES,
                { mkpc: type, filters: savedFilters },
            );
        } else {
            const formatFilters = { ...filters };
            // console.log({ type });
            if (!type.preload && !hasUsableFilter(formatFilters)) {
                return { type: types.SKIP, payload: { mkpc: type } };
            }

            Object.entries(formatFilters).forEach(([key, value]) => {
                if (value && (key.startsWith('before') || key.startsWith('after'))) {
                    formatFilters[key] = DateUtils.dateToSQlDateTime(new Date(value as string));
                }
            });

            const savedFilters = {
                page: page + 1,
                ...filters,
            } as any;

            if (order) {
                savedFilters.sort = order.property;
                savedFilters.order = order.nextOrder;
            }
            return fetchAction(
                CoreAPIService.get(
                    `/list/${type.url}?${order ? `sort=${order.property}&order=${order.nextOrder}&` : ''}page=${
                        page + 1
                    }&num_per_page=${rowsPerPage ?? DEFAULT_ROW_NUMBER}${URLUtils.urlToFilter(formatFilters)}`,
                ),
                types.GET_PAGES,
                { mkpc: type, filters: savedFilters },
            );
        }
    },
};

const localState = (state: any): TableState => state[name] || initialState;
export const selectors = {
    data: (state) => localState(state),
    getDatas: (state, props) => selectors.data(state)[props.type.reducer]?.data || [],
    getMetas: (state, props) => selectors.data(state)[props.type.reducer]?.meta || {},
    getFilters: (state, props) => selectors.data(state).saved_filters[`${props.type.url}_${props.type.reducer}`] || {},
};

export const makeMemoizedGetDatas = () => {
    return createSelector(selectors.getDatas, (datas) => datas);
};

export const makeMemoizedGetMetas = () => {
    return createSelector(selectors.getMetas, (metas) => metas);
};

export const makeMemoizedGetFilters = () => {
    return createSelector(selectors.getFilters, (filters) => filters);
};
